import { memo, useCallback } from 'react';

import { type Board } from '@air/api/types';

import { type ItemProps } from '~/components/Gallery/types';
import {
  EditableTableTitle,
  type EditableTableTitleProps,
} from '~/components/TableView/NameCell/NameCellContent/EditableTableTitle';
import { useUpdateBoard } from '~/swr-hooks/boards/useUpdateBoard';
import { BoardMetaData } from '../NameCell/BoardNameCell/BoardMetaData';
import { PrivateTableAssetTitle } from '../NameCell/NameCellContent/PrivateTableAssetTitle';
import { useIsItemRenaming, useRenamingContext } from './TableViewItemRenamingContext';

export type PrivateBoardNameCellTitleProps = {
  onClick: ItemProps['onItemClick'];
  onCmdClick?: ItemProps['onItemCmdClick'];
  board: Board;
};

export const PrivateBoardNameCellTitle = memo(({ onClick, onCmdClick, board }: PrivateBoardNameCellTitleProps) => {
  const {
    updateBoard: { mutate: updateBoard },
  } = useUpdateBoard();
  const { setIsRenaming } = useRenamingContext();
  const isCurrentlyRenaming = useIsItemRenaming(board.id);

  const onRenameComplete = useCallback<EditableTableTitleProps['onRenameComplete']>(
    (title) => {
      updateBoard({ board, trackLocation: 'table-cell', values: { title } });
      setIsRenaming(null);
    },
    [board, setIsRenaming, updateBoard],
  );

  return (
    <div
      className="relative ml-3 w-full"
      style={{
        zIndex: isCurrentlyRenaming ? 9999 : undefined,
      }}
      data-disableselect="true"
      onDoubleClick={(e) => {
        if (isCurrentlyRenaming) e.stopPropagation();
      }}
      onMouseDown={(e) => {
        if (isCurrentlyRenaming) e.stopPropagation();
      }}
      draggable={isCurrentlyRenaming ? true : undefined}
      onDragStart={(e) => {
        if (isCurrentlyRenaming) e.preventDefault();
      }}
    >
      {isCurrentlyRenaming ? (
        <EditableTableTitle
          title={board.title}
          setIsRenaming={() => setIsRenaming(null)}
          onRenameComplete={onRenameComplete}
        />
      ) : (
        <>
          <PrivateTableAssetTitle title={board.title} onClick={onClick} onCmdClick={onCmdClick} />
          <BoardMetaData itemCount={board.directDescendantCount} />
        </>
      )}
    </div>
  );
});

PrivateBoardNameCellTitle.displayName = 'PrivateBoardNameCellTitle';
